/**
 * useArticles.js
 * Composable แทน articlesData.js
 * ดึงข้อมูลจาก Hasura — table: articles (schema: articles)
 *
 * วิธีใช้:
 *   import { useArticles, useArticleById } from "@/composables/useArticles"
 *
 *   // ดึงทุกบทความ (ใช้ใน News list page / card list)
 *   const { articles, loading, error } = useArticles()
 *
 *   // ดึงบทความเดียว (ใช้ใน ArticleDetail page)
 *   const { article, loading, error } = useArticleById(id)
 */

import { computed } from "vue";
import { useQuery } from "@vue/apollo-composable";
import gql from "graphql-tag";

// ─── Helper: แปลง timestamp → ข้อความวันที่ภาษาลาว ──────────────────────────
const LAO_MONTHS = [
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
];

function formatDate(timestamp) {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  const day = d.getDate();
  const month = LAO_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

// ─── Helper: map raw DB row → structure เดิมที่ component ใช้อยู่ ────────────
function mapArticle(raw) {
  return {
    id: raw.id, // uuid (ใช้ใน router)
    category: raw.category,
    title: raw.title,
    excerpt: raw.excerpt,
    readTime: raw.readTime, // ชื่อ field ตรงกับ DB
    date: formatDate(raw.date), // แปลง timestamp → "20 ກຸມພາ 2026"
    cardImageCover: raw.cardImageCover,
    content: raw.content ?? [], // JSONB → array พร้อมใช้เลย
  };
}

// ─── Query 1: ดึงทุกบทความ (สำหรับ list / card) ─────────────────────────────
const GET_ALL_ARTICLES = gql`
  query GetAllArticles {
    articles_article(order_by: { date: desc }) {
      id
      category
      title
      excerpt
      readTime
      date
      cardImageCover
    }
  }
`;

// ─── Query 2: ดึงบทความเดียวพร้อม content (สำหรับ detail page) ───────────────
const GET_ARTICLE_BY_ID = gql`
  query GetArticleById($id: uuid!) {
    articles_article_by_pk(id: $id) {
      id
      category
      title
      excerpt
      readTime
      date
      cardImageCover
      content
    }
  }
`;

// ─── Composable 1: useArticles ────────────────────────────────────────────────
export function useArticles() {
  const { result, loading, error } = useQuery(GET_ALL_ARTICLES, null, {
    fetchPolicy: "network-only",
  });

  // map ทุก row
  const articles = computed(() =>
    (result.value?.articles_article ?? []).map(mapArticle),
  );

  return { articles, loading, error };
}

// ─── Composable 2: useArticleById ────────────────────────────────────────────
export function useArticleById(id) {
  const { result, loading, error } = useQuery(
    GET_ARTICLE_BY_ID,
    () => ({ id }), // reactive — id เปลี่ยน query ทำงานใหม่อัตโนมัติ
    {
      fetchPolicy: "network-only",
      enabled: computed(() => !!id), // ไม่ query ถ้า id ว่าง
    },
  );

  const article = computed(() => {
    const raw = result.value?.articles_article_by_pk;
    return raw ? mapArticle(raw) : null;
  });

  return { article, loading, error };
}
